import { afterNextRender, Directive, effect, ElementRef, Injector, input, Renderer2 } from "@angular/core";

@Directive({
    selector: "[highlight]",
    standalone: true,
})
export class HighlightDirective {
    highlight = input<string>(""); // Search term to highlight

    private originalText: string = "";

    constructor(
        private el: ElementRef,
        private renderer: Renderer2,
        private injector: Injector
    ) {
        // Use afterNextRender to ensure DOM content is ready
        afterNextRender(
            () => {
                // Capture the original text after initial render
                this.originalText = this.el.nativeElement.textContent || "";

                // Now set up the effect to watch for search term changes
                effect(
                    () => {
                        this.updateHighlight(this.highlight());
                    },
                    { injector: this.injector }
                );
            },
            { injector: this.injector }
        );
    }

    private updateHighlight(searchTerm: string) {
        const element = this.el.nativeElement;
        const term = String(searchTerm ?? "").trim();

        // Clear existing content
        this.renderer.setProperty(element, "innerHTML", "");

        if (!term || !this.originalText) {
            this.renderer.setProperty(element, "textContent", this.originalText);
            return;
        }

        // Clear existing content
        this.renderer.setProperty(element, "innerHTML", "");

        // Escape special regex characters in search term
        const escapedTerm = term.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
        const regex = new RegExp(`(${escapedTerm})`, "gi");

        // Split text by matches and create HTML with highlighted spans
        const parts = this.originalText.split(regex);

        parts.forEach((part) => {
            if (!part) return;

            const matches = part.toLowerCase() === term.toLowerCase();

            if (matches) {
                // Create highlighted span with 'text-highlight' class
                const span = this.renderer.createElement("span");
                this.renderer.addClass(span, "text-highlight");
                this.renderer.setProperty(span, "textContent", part);
                this.renderer.appendChild(element, span);
            } else {
                // Create text node for non-matching text
                const textNode = this.renderer.createText(part);
                this.renderer.appendChild(element, textNode);
            }
        });
    }
}

